import React, { useState } from 'react';
import {
  VStack, SimpleGrid, FormControl, FormLabel, Input, Select, Textarea,
  Button, HStack, useToast,
} from '@chakra-ui/react';
import { ImmDecision, crearDecision } from '../../../api/immApi';
import {
  SectionTitle, Field, fmtDateTime, ExpedienteSelect,
  TIPO_DECISION_LABEL, IMM_COLOR, IMM_COLOR_HOVER,
} from '../_shared';

type Modo = 'new' | 'view';

interface Props {
  modo: Modo;
  data?: ImmDecision;
  onGuardado?: (d: ImmDecision) => void;
  onCancelar?: () => void;
}

const hoy = () => new Date().toISOString().split('T')[0];

const FormularioDecision: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const [guardando, setGuardando] = useState(false);
  const [expedienteId, setExpedienteId] = useState<number | undefined>(data?.expedienteId);

  const [f, setF] = useState<Partial<ImmDecision>>({
    fecha: data?.fecha ? data.fecha.split('T')[0] : hoy(),
    tipoDecision: data?.tipoDecision || '',
    observaciones: data?.observaciones || '',
  });

  const set = (campo: keyof ImmDecision, valor: any) => setF((prev) => ({ ...prev, [campo]: valor }));

  if (modo === 'view') {
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Datos de la decisión</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Field label="Expediente" value={data?.expediente?.folio ?? (data ? `#${data.expedienteId}` : '—')} />
          <Field label="Fecha" value={fmtDateTime(data?.fecha)} />
          <Field label="Decisión" value={data ? (TIPO_DECISION_LABEL[data.tipoDecision] ?? data.tipoDecision) : '—'} />
          <Field label="Registró" value={data?.usuarioRegistra} />
        </SimpleGrid>
        <SectionTitle>Observaciones</SectionTitle>
        <Field label="Observaciones" value={data?.observaciones} />
      </VStack>
    );
  }

  const handleGuardar = async () => {
    if (!expedienteId) {
      toast({ title: 'El expediente es obligatorio', status: 'warning', duration: 3000 });
      return;
    }
    if (!f.tipoDecision) {
      toast({ title: 'El tipo de decisión es obligatorio', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const payload: Partial<ImmDecision> = { ...f, expedienteId };
      const res = await crearDecision(payload);
      toast({ title: 'Decisión registrada', status: 'success', duration: 3000 });
      onGuardado?.(res);
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo registrar la decisión.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos de la decisión</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Expediente</FormLabel>
          <ExpedienteSelect value={expedienteId} onChange={setExpedienteId} isDisabled={modo !== 'new'} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha</FormLabel>
          <Input type="date" value={(f.fecha as string) || ''} onChange={(e) => set('fecha', e.target.value)} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Tipo de decisión</FormLabel>
          <Select placeholder="Selecciona" value={f.tipoDecision || ''} onChange={(e) => set('tipoDecision', e.target.value)}>
            {Object.entries(TIPO_DECISION_LABEL).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </Select>
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Observaciones</FormLabel>
          <Textarea rows={3} value={f.observaciones || ''} onChange={(e) => set('observaciones', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Registrar decisión
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioDecision;
